import { authorizeUrl, consumeFlow, createPkceChallenge, redirectUriFor, rememberFlow, safeReturnPath } from '@/lib/pkce.ts';
import type { ConsoleAuthConfig } from '@/lib/pkce.ts';

/**
 * The console's sign-in, from the button to a token in memory.
 *
 * `pkce.ts` is the protocol arithmetic — verifier, challenge, state, and the one storage slot that
 * has to survive a redirect. This module is the flow built on it: where the configuration comes
 * from, what is exchanged, and what is deliberately thrown away.
 *
 * **The refresh token is discarded.** Cognito returns one and there is nowhere safe to put it:
 * §0.2 rule 2 bans web storage, and a refresh token in memory buys nothing a re-auth does not,
 * because it dies with the tab either way. When the ID token expires the console redirects
 * through the pool again — which the user does not see, because the pool's own session cookie
 * answers immediately. That is §7's "silent re-auth", and it is one redirect rather than a
 * long-lived credential in the browser.
 */

export type { ConsoleAuthConfig } from '@/lib/pkce.ts';

export interface ConsoleIdentity {
  /** The Cognito ID token. Sent as the bearer; the control plane is what verifies it. */
  token: string;
  email: string;
  /** Epoch milliseconds, read from the token's own `exp`. See `expiryOf`. */
  expiresAt: number;
}

/**
 * The pool this console signs into, written beside the bundle at deploy time.
 *
 * Absent in local development and in any environment deployed without a console pool, and that is
 * a supported state rather than an error: the sign-in button is simply not offered, and an API
 * token is still a way in. Returning `null` rather than throwing is what makes that distinction
 * available to the caller.
 */
export async function loadAuthConfig(fetcher: typeof fetch = fetch): Promise<ConsoleAuthConfig | null> {
  let response: Response;
  try {
    response = await fetcher('/config.json', { headers: { accept: 'application/json' } });
  } catch {
    return null;
  }
  if (!response.ok) return null;

  let body: Partial<ConsoleAuthConfig>;
  try {
    body = await response.json();
  } catch {
    // The SPA fallback serves index.html for any unknown path, so a missing config.json arrives
    // as a 200 full of HTML. Parsing it is how that is told apart from a real configuration.
    return null;
  }

  // All four or none. A partial configuration produces an authorize URL that redirects to a
  // Cognito error page, which is a much worse thing to hand a user than no button at all.
  const { region, userPoolId, clientId, domain } = body;
  if (!region || !userPoolId || !clientId || !domain) return null;

  return { region, userPoolId, clientId, domain };
}

/** Where the browser is right now, as a path this app will navigate back to. */
export function currentPath(location: Pick<Location, 'pathname' | 'search'> = window.location): string {
  return safeReturnPath(`${location.pathname}${location.search}`);
}

/**
 * Starts a sign-in: mints a challenge, remembers it, and leaves for the hosted UI.
 *
 * Does not return — the assignment navigates away. Typed `Promise<void>` because minting the
 * challenge is async, not because there is anything to wait for afterwards.
 */
export async function beginSignIn(
  config: ConsoleAuthConfig,
  returnTo: string,
  target: { origin: string; assign(url: string): void } = window.location,
): Promise<void> {
  const flow = await createPkceChallenge();
  rememberFlow(flow, returnTo);
  target.assign(authorizeUrl(config, flow, redirectUriFor(target.origin)));
}

export interface CallbackParams {
  code?: string;
  state?: string;
  error?: string;
  errorDescription?: string;
}

export function readCallbackParams(search: string): CallbackParams {
  const params = new URLSearchParams(search);
  // Built by assignment rather than as a literal: `exactOptionalPropertyTypes` distinguishes an
  // absent key from one set to `undefined`, and every consumer here treats them the same.
  const found: CallbackParams = {};
  for (const [name, key] of [
    ['code', 'code'], ['state', 'state'], ['error', 'error'], ['error_description', 'errorDescription'],
  ] as const) {
    const value = params.get(name);
    if (value !== null) found[key] = value;
  }
  return found;
}

export class SignInFailed extends Error {
  readonly reason: string;

  constructor(reason: string, message: string) {
    super(message);
    this.name = 'SignInFailed';
    this.reason = reason;
  }
}

/**
 * Completes the redirect: checks the state, exchanges the code, and returns the identity.
 *
 * The state comparison is the CSRF defence and it happens **before** the code is sent anywhere.
 * A code paired with a state this tab did not mint is a code somebody else obtained, and
 * exchanging it would sign this browser in as them.
 *
 * `consumeFlow` clears the stored attempt on read whatever happens next, so a failure here cannot
 * leave a verifier behind for a second attempt to pair with a different code.
 */
export async function completeSignIn(
  config: ConsoleAuthConfig,
  params: CallbackParams,
  options: { origin?: string; fetcher?: typeof fetch } = {},
): Promise<{ identity: ConsoleIdentity; returnTo: string }> {
  const pending = consumeFlow();

  if (params.error) {
    throw new SignInFailed(params.error, params.errorDescription ?? `Sign-in was refused: ${params.error}.`);
  }
  if (!params.code || !params.state) {
    throw new SignInFailed('incomplete', 'That sign-in link is missing its authorization code.');
  }
  if (!pending) {
    throw new SignInFailed('no-flow', 'This tab has no sign-in in progress. Start again from the console.');
  }
  if (pending.state !== params.state) {
    throw new SignInFailed('state-mismatch', 'That sign-in did not start in this tab. Start again from the console.');
  }

  const origin = options.origin ?? window.location.origin;
  const fetcher = options.fetcher ?? fetch;

  const response = await fetcher(`https://${config.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code: params.code,
      redirect_uri: redirectUriFor(origin),
      code_verifier: pending.verifier,
    }).toString(),
  });

  if (!response.ok) {
    throw new SignInFailed('exchange-failed', 'The sign-in could not be completed. Try again.');
  }

  const body = await response.json() as { id_token?: string };
  if (!body.id_token) throw new SignInFailed('no-id-token', 'The pool returned no identity token.');

  // `body.refresh_token` is deliberately not read. See the note at the top of this file.
  const claims = readClaims(body.id_token);
  const email = typeof claims.email === 'string' ? claims.email.trim().toLowerCase() : '';
  if (!email) throw new SignInFailed('no-email', 'That account has no email address on it.');

  return {
    identity: { token: body.id_token, email, expiresAt: expiryOf(claims) },
    returnTo: pending.returnTo,
  };
}

/**
 * Reads a token's claims **without verifying it**.
 *
 * Deliberate, and safe only because of what it is used for: the email to show in the header, and
 * the moment to re-auth. Nothing here grants anything. The control plane verifies the signature,
 * the issuer, the audience, and the expiry on every single request, and a browser that lied to
 * itself about any of them gets a 401 for its trouble.
 */
function readClaims(token: string): Record<string, unknown> {
  const payload = token.split('.')[1];
  if (!payload) return {};
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * When to re-auth.
 *
 * A token with no readable `exp` is treated as expiring now rather than never: the failure that
 * follows is one redirect, and the alternative is a console that keeps presenting a credential
 * the API has been rejecting for an hour.
 */
function expiryOf(claims: Record<string, unknown>): number {
  return typeof claims.exp === 'number' ? claims.exp * 1000 : Date.now();
}

/** Milliseconds until a re-auth should start, never negative. Re-auth runs a minute early. */
export function millisecondsUntilReauth(expiresAt: number, now = Date.now()): number {
  return Math.max(0, expiresAt - 60_000 - now);
}
